import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, ChevronLeft } from "lucide-react";

const emptySeries = { name: "", slug: "", description: "", target_lessons: 22, order: 0 };
const emptyLesson = { lesson_number: 1, title: "", summary: "", youtube_url: "", slides_url: "", student_sheet_url: "", body: "" };

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--olive)" }}>{label}</Label>
      {children}
    </div>
  );
}

export default function AdminSeries() {
  const [series, setSeries] = useState([]);
  const [selected, setSelected] = useState(null); // selected series object
  const [lessons, setLessons] = useState([]);
  const [seriesModal, setSeriesModal] = useState(null); // {mode, data}
  const [lessonModal, setLessonModal] = useState(null); // {mode, data}

  const loadSeries = async () => {
    const { data } = await api.get("/admin/series");
    setSeries(data);
  };
  const loadLessons = async (sid) => {
    const { data } = await api.get(`/admin/series/${sid}/lessons`);
    setLessons(data);
  };

  useEffect(() => { loadSeries(); }, []);
  useEffect(() => { if (selected) loadLessons(selected.id); }, [selected]);

  const saveSeries = async () => {
    const { mode, data } = seriesModal;
    try {
      const payload = { ...data, target_lessons: parseInt(data.target_lessons) || 0, order: parseInt(data.order) || 0 };
      if (mode === "create") await api.post("/admin/series", payload);
      else await api.put(`/admin/series/${data.id}`, payload);
      toast.success("Saved.");
      setSeriesModal(null);
      loadSeries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const deleteSeries = async (id) => {
    if (!window.confirm("Delete this series AND all its lessons? Cannot be undone.")) return;
    await api.delete(`/admin/series/${id}`);
    toast.success("Deleted.");
    setSelected(null);
    loadSeries();
  };

  const saveLesson = async () => {
    const { mode, data } = lessonModal;
    try {
      const payload = { ...data, series_id: selected.id, lesson_number: parseInt(data.lesson_number) || 1 };
      if (mode === "create") await api.post("/admin/lessons", payload);
      else await api.put(`/admin/lessons/${data.id}`, payload);
      toast.success("Saved.");
      setLessonModal(null);
      loadLessons(selected.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    await api.delete(`/admin/lessons/${id}`);
    toast.success("Deleted.");
    loadLessons(selected.id);
  };

  // -------- LIST OF SERIES --------
  if (!selected) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>Study series</h2>
          <button onClick={() => setSeriesModal({ mode: "create", data: { ...emptySeries } })} data-testid="admin-add-series-button" className="btn-primary">
            <Plus size={14} /> New series
          </button>
        </div>
        <div className="space-y-3">
          {series.map((s) => (
            <div key={s.id} data-testid={`admin-series-${s.id}`} className="card-surface p-5 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="overline" style={{ fontSize: "0.65rem" }}>Slug: /{s.slug} · Target {s.target_lessons || "—"}</div>
                <h3 className="font-serif-display text-xl leading-tight" style={{ color: "var(--ink)" }}>{s.name}</h3>
                <p className="text-sm truncate" style={{ color: "var(--ink-2)" }}>{s.description}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setSelected(s)} data-testid={`admin-series-${s.id}-open`} className="btn-primary">Manage lessons →</button>
                <button onClick={() => setSeriesModal({ mode: "edit", data: { ...s } })} className="btn-ghost"><Pencil size={14} /></button>
                <button onClick={() => deleteSeries(s.id)} className="btn-ghost" style={{ color: "var(--ember)", borderColor: "var(--ember)" }}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Series editor modal */}
        <Dialog open={!!seriesModal} onOpenChange={(o) => !o && setSeriesModal(null)}>
          <DialogContent className="max-w-xl" style={{ background: "var(--bg-base)" }}>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">{seriesModal?.mode === "create" ? "New series" : "Edit series"}</DialogTitle>
              <DialogDescription className="text-sm" style={{ color: "var(--muted)" }}>Slug becomes the URL: /dashboard/series/your-slug</DialogDescription>
            </DialogHeader>
            {seriesModal && (
              <div className="space-y-4">
                <Field label="Name"><Input data-testid="admin-series-name-input" value={seriesModal.data.name} onChange={(e) => setSeriesModal({ ...seriesModal, data: { ...seriesModal.data, name: e.target.value } })} /></Field>
                <Field label="Slug (URL-safe, e.g. first-steps)"><Input data-testid="admin-series-slug-input" value={seriesModal.data.slug} onChange={(e) => setSeriesModal({ ...seriesModal, data: { ...seriesModal.data, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") } })} /></Field>
                <Field label="Description"><Textarea data-testid="admin-series-description-input" rows={4} value={seriesModal.data.description} onChange={(e) => setSeriesModal({ ...seriesModal, data: { ...seriesModal.data, description: e.target.value } })} /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Target lessons (e.g. 22)"><Input data-testid="admin-series-target-input" type="number" value={seriesModal.data.target_lessons} onChange={(e) => setSeriesModal({ ...seriesModal, data: { ...seriesModal.data, target_lessons: e.target.value } })} /></Field>
                  <Field label="Order (lower = first)"><Input data-testid="admin-series-order-input" type="number" value={seriesModal.data.order} onChange={(e) => setSeriesModal({ ...seriesModal, data: { ...seriesModal.data, order: e.target.value } })} /></Field>
                </div>
              </div>
            )}
            <DialogFooter>
              <button onClick={() => setSeriesModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveSeries} data-testid="admin-series-save" className="btn-primary">Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // -------- LESSONS OF SELECTED SERIES --------
  return (
    <div data-testid="admin-series-lessons-view">
      <button onClick={() => setSelected(null)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-6" style={{ color: "var(--ink-2)" }}>
        <ChevronLeft size={14} /> All series
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">{selected.name}</div>
          <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>
            Lessons ({lessons.length}{selected.target_lessons ? ` / ${selected.target_lessons}` : ""})
          </h2>
        </div>
        <button onClick={() => setLessonModal({ mode: "create", data: { ...emptyLesson, lesson_number: lessons.length + 1 } })} data-testid="admin-add-lesson-button" className="btn-primary">
          <Plus size={14} /> Add lesson
        </button>
      </div>

      <div className="space-y-3">
        {lessons.map((l) => (
          <div key={l.id} data-testid={`admin-lesson-${l.id}`} className="card-surface p-5 flex items-center gap-4">
            <div className="font-serif-display text-3xl shrink-0" style={{ color: "var(--ember)" }}>
              {String(l.lesson_number).padStart(2, "0")}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif-display text-lg leading-tight" style={{ color: "var(--ink)" }}>{l.title}</h3>
              <p className="text-sm truncate" style={{ color: "var(--ink-2)" }}>{l.summary}</p>
              <div className="mt-1 text-[0.65rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
                {l.youtube_url && "YouTube ✓ "} {l.slides_url && "Slides ✓ "} {l.student_sheet_url && "Sheet ✓ "}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setLessonModal({ mode: "edit", data: { ...l } })} className="btn-ghost"><Pencil size={14} /></button>
              <button onClick={() => deleteLesson(l.id)} className="btn-ghost" style={{ color: "var(--ember)", borderColor: "var(--ember)" }}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {lessons.length === 0 && <p style={{ color: "var(--muted)" }}>No lessons yet. Click "Add lesson" to begin.</p>}
      </div>

      {/* Lesson editor modal */}
      <Dialog open={!!lessonModal} onOpenChange={(o) => !o && setLessonModal(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg-base)" }}>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">{lessonModal?.mode === "create" ? "New lesson" : "Edit lesson"}</DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "var(--muted)" }}>Paste public share links — YouTube, Google Slides, and the Student Sheet (Google Doc / PDF).</DialogDescription>
          </DialogHeader>
          {lessonModal && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Field label="Lesson #"><Input data-testid="admin-lesson-number-input" type="number" value={lessonModal.data.lesson_number} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, lesson_number: e.target.value } })} /></Field>
                <div className="col-span-3">
                  <Field label="Title"><Input data-testid="admin-lesson-title-input" value={lessonModal.data.title} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, title: e.target.value } })} /></Field>
                </div>
              </div>
              <Field label="Short description"><Textarea data-testid="admin-lesson-summary-input" rows={3} value={lessonModal.data.summary} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, summary: e.target.value } })} /></Field>
              <Field label="YouTube URL"><Input data-testid="admin-lesson-youtube-input" placeholder="https://www.youtube.com/watch?v=..." value={lessonModal.data.youtube_url || ""} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, youtube_url: e.target.value } })} /></Field>
              <Field label="Google Slides URL"><Input data-testid="admin-lesson-slides-input" placeholder="https://docs.google.com/presentation/d/..." value={lessonModal.data.slides_url || ""} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, slides_url: e.target.value } })} /></Field>
              <Field label="Student Sheet URL"><Input data-testid="admin-lesson-sheet-input" placeholder="https://docs.google.com/document/d/..." value={lessonModal.data.student_sheet_url || ""} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, student_sheet_url: e.target.value } })} /></Field>
              <Field label="Notes / body (optional)"><Textarea data-testid="admin-lesson-body-input" rows={6} value={lessonModal.data.body || ""} onChange={(e) => setLessonModal({ ...lessonModal, data: { ...lessonModal.data, body: e.target.value } })} /></Field>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setLessonModal(null)} className="btn-ghost">Cancel</button>
            <button onClick={saveLesson} data-testid="admin-lesson-save" className="btn-primary">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
